/**
 * @module main
 * @description Main entry point for @ufe/shared package.
 * Aggregates all constants and utilities from the UFE monorepo shared project.
 * This module provides a unified interface for accessing shared functionality
 * across browser and node environments.
 *
 * @requires constants/catalog - Catalog-related constants
 * @requires constants/constants - Core UFE constants
 * @requires constants/env - Environment detection and configuration
 * @requires utils/timestamp - Timestamp formatting utilities
 * @requires utils/string - String manipulation utilities
 * @requires utils/url - URL validation and sanitization utilities
 *
 * @example
 * // Import everything
 * import { constants, utils } from '@ufe/shared';
 *
 * @example
 * // Use specific exports
 * import { constants } from '@ufe/shared';
 * const isProduction = constants.env.isUfeEnvProduction;
 */

import * as catalog from 'constants/catalog';
import * as consts from 'constants/constants';
import * as env from 'constants/env';
import printTimestamp from 'utils/timestamp';
import * as string from 'utils/string';
import * as url from 'utils/url';

/**
 * @constant {string} version - Package version
 */
const version = '1.0.0';

/**
 * @constant {Object} BuildTargetEnvironment - Build target environment identifiers
 * @property {string} Browser - Browser environment identifier
 * @property {string} Node - Node.js environment identifier
 */
const BuildTargetEnvironment = {
    Browser: 'Browser',
    Node: 'Node'
} as const;

type BuildTargetEnvironmentType = (typeof BuildTargetEnvironment)[keyof typeof BuildTargetEnvironment];

let targetEnvironment: BuildTargetEnvironmentType = BuildTargetEnvironment.Browser;

if (__NODE_BUILD__) {
    targetEnvironment = BuildTargetEnvironment.Node;
}

/**
 * @constant {string} description - Package description including build environment
 */
const description = `Shared project for @ufe monorepo. ${targetEnvironment} environment build.`;

/**
 * @constant {Object} constants - Aggregated constants from all constant modules
 * @property {Object} catalog - Catalog-related constants (PAGE_TYPES, REFINEMENT_STATES, etc.)
 * @property {Object} env - Environment detection utilities and constants
 * @property {string} UFE_ENV_PRODUCTION - Production environment constant
 * @property {string} UFE_ENV_QA - QA environment constant
 * @property {string} UFE_ENV_LOCAL - Local environment constant
 * @property {string} NODE_ENV_PRODUCTION - Node production environment constant
 * @property {string} NODE_ENV_DEVELOPMENT - Node development environment constant
 * @property {string} DOCUMENT_TYPE - HTML document type declaration
 * @property {string} RENDERTYPE - Render type constant
 */
const constants = {
    ...consts,
    catalog,
    env
};

/**
 * @constant {Object} utils - Aggregated utility functions
 * @property {Function} printTimestamp - Format current time as YYYY-MM-DD HH:mm:ss,SSS
 * @property {Object} string - String manipulation utilities (capitalize, kebabCase, camelCase)
 * @property {Object} url - URL validation and sanitization utilities
 */
const utils = {
    printTimestamp,
    string,
    url
};

// // Restricted code syntax:
// // 1. Enums
// enum BuildTargetEnvironment {
//     Browser = 'Browser',
//     Node = 'Node'
// }

// // 2. Namespaces
// namespace ExampleNamespace {
//     export const sampleFunction = () => {
//         return 'This is inside a namespace';
//     };

//     export interface SampleInterface {
//         id: number;
//         name: string;
//     }
// }

// // 3. Parameter Properties (should be restricted)
// class _UserService {
//     constructor(
//         private readonly _apiUrl: string,
//         public _userId: number,
//         protected _logger: Console,
//         _justAParam: string
//     ) {
//         // Parameter properties automatically create and assign class properties
//     }

//     getUser() {
//         return `Fetching user ${this._userId} from ${this._apiUrl}`;
//     }
// }

export { constants, description, utils, version };
